import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ProjectService } from '../services/project.service';
import { ProjectResponseDto } from '../../features/dashboard/models/project.dto';
import { ProjectMembershipService } from '../services/project-membership.service';

/**
 * Loads project context from query params for deep-link support.
 * If ?projectId=<uuid> is present and differs from the current project,
 * fetches project data + membership and calls projectService.setProject().
 */
export const projectContextGuard: CanActivateFn = (route) => {
  const projectId = route.queryParamMap.get('projectId');
  if (!projectId) {
    return true;
  }

  const projectService = inject(ProjectService);
  if (projectService.projectId === projectId) {
    return true;
  }

  const http = inject(HttpClient);
  const projectMembershipService = inject(ProjectMembershipService);
  const apiUrl = `${environment.apiHost}/api`;

  return forkJoin({
    project: http.get<ProjectResponseDto>(`${apiUrl}/projects/${projectId}/`),
    // Routed through the service (not a raw http.get) so it shares its cache/in-flight
    // de-dupe with the layout's own loadForProject() call for the same project id. The role it
    // returns stays there — copying it into ProjectService is what gave the app a second,
    // storage-backed copy of a fact someone else can revoke.
    membership: projectMembershipService.loadForProject(projectId)
  }).pipe(
    map(({ project }) => {
      if (project.status === 'success' && project.data) {
        projectService.setProject({
          id: project.data.id,
          name: project.data.name,
          description: project.data.description,
          color: project.data.color,
          logo: project.data.logo || null,
          created_at: project.data.created_at,
          updated_at: project.data.updated_at
        });
      }
      return true;
    }),
    catchError(() => of(true))
  );
};
